import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import {
  Building,
  Invoice,
  InvoiceHeader,
  InvoiceLine,
  InvoiceOrganization,
  InvoicePlace,
  Member, Organization,
} from '../models';
import { InvoicingService } from '../utils/invoice/invoicing.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MemberService } from '../data';

@Injectable()
export class InvoiceFormService {

  get recipient(): Member {
    return this._recipient.value;
  }

  set recipient(owner: Member) {
    this._recipient.patchValue(owner);
  }
  headerGroup: FormGroup;

  invoiceForm: FormGroup;
  linesGroup: FormGroup;

  private _recipient: FormControl = new FormControl('', [
    Validators.required,
  ]);

  constructor(
    private formBuilder: FormBuilder,
    private memberService: MemberService,
    private invoicingService: InvoicingService,
  ) {
    this.headerGroup = this.formBuilder.group({
      recipient: this._recipient,
      comments: [''],
      isInvoice: [''],
    });

    this.linesGroup = this.formBuilder.group({
      lines: this.formBuilder.array([]),
    }, {
      validators: [this.noLinesAdded],
    });

    this.invoiceForm = this.formBuilder.group({
      headerGroup: this.headerGroup,
      linesGroup: this.linesGroup,
    });
  }

  /**
   * Returns empty invoice line for lines form array
   */
  createEmptyInvoiceLine(): FormGroup {
    return this.formBuilder.group({
      name: ['', Validators.required],
      type: ['', [this.typeInvalid, Validators.required]],
      quantity: ['', [Validators.required, Validators.min(1)]],
      unitAmount: ['', [Validators.required, Validators.min(0)]],
      currencyCode: ['', Validators.required],
    });
  }

  /**
   * Returns invoice object based on form passed as parameter
   *
   * @param form invoice form
   */
  getInvoiceFromForm(form: FormGroup): Observable<Invoice> {
    const invoice = new Invoice();

    const invoicerWorkPlace = this.memberService.getUser().workplace as Building;
    const invoicerOrganization = invoicerWorkPlace.manager as Organization;

    invoice.process = 'DKX';
    invoice.header = this.getInvoiceHeaderFromForm(form);
    invoice.supplier = null;
    invoice.supplierEco = new InvoicePlace({ costCenter: invoicerOrganization.costCenter });
    invoice.lines = this.getInvoiceLinesFormForm(form);

    return this.invoicingService.getInvoiceableNumber(this.recipient).pipe(
      map(invoiceableNumber => {
        invoice.customer = invoiceableNumber instanceof InvoiceOrganization ? invoiceableNumber : null;
        invoice.customerEco = invoiceableNumber instanceof InvoicePlace ? invoiceableNumber : null;
        return invoice;
      }),
    );
  }

  /**
   * Returns invoice header based on form passed as parameter
   *
   * @param form invoice form
   */
  private getInvoiceHeaderFromForm(form: FormGroup): InvoiceHeader {
    const header = new InvoiceHeader();
    const headerGroup = form.get('headerGroup');

    header.documentType = headerGroup.get('isInvoice').value;
    header.dates = {
      operationDate: new Date().toISOString(),
    };
    header.comments = headerGroup.get('comments').value;
    header.customerEmail = this.recipient.getEmail();
    header.customerContact = this.recipient.getFullName();

    return header;
  }

  /**
   * Returns invoice lines based on form passed as parameter
   *
   * @param form invoice form
   */
  private getInvoiceLinesFormForm(form: FormGroup): InvoiceLine[] {
    const lines: InvoiceLine[] = [];

    for (const formLine of (form.get('linesGroup').get('lines') as FormArray).controls) {
      const line = new InvoiceLine();
      line.billingObject = formLine.get('type').value;
      line.itemDescription = formLine.get('name').value;
      line.price.invoiceAmount.currency = formLine.get('currencyCode').value;
      line.price.invoiceAmount.unitAmount = parseFloat(formLine.get('unitAmount').value);
      line.price.quantity = parseInt(formLine.get('quantity').value, 10);
      line.price.vatCode = 1;

      lines.push(line);
    }

    return lines;
  }

  /**
   * Validator that triggers if guests number exceeds room capacity
   *
   * @param formGroup
   */
  private noLinesAdded: ValidatorFn = (formGroup: FormGroup): { [key: string]: any } | null => {
    const lines = formGroup.get('lines') as FormArray;

    if (!lines) {
      return null;
    }

    return lines.controls.length > 0 ? null : {noInvoiceLinesAdded: true};
  };

  /**
   * Validator that triggers if user submitted a type that is not valid in this form
   *
   * @param control
   */
  private typeInvalid: ValidatorFn = (control: FormControl): { [key: string]: any } | null => {
    if (!this.invoicingService.validTypes.includes(control.value)) {
      return {typeInvalid: true};
    }

    return null;
  };
}

